/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/featuresSection.config.json";
import axios from "axios";

import { useProduct } from "../provider";
import { toHeaderCase } from "js-convert-case";

export interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;
  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const { prodId, auth } = useProduct();
    try {
      const res = await axios.post(`${args?.apiURL}/provenance/full_provenance_v3/no_auth/`, {
        barcode: prodId,
        appMode: "web",
        scanType: "QR",
      });
      if (res?.data) {
        console.log(`productPage productData`, res?.data?.data);
        let _res = res?.data?.data;
        return {
          productData: {
            ...res?.data,
            title: _res?.product?.title,
            auth: auth === "200" ? config?.lockIcon : auth === "500" ? config?.unlockIcon : null,
            subtitle: _res?.product?.subtitle,
            heroImage: {
              title: _res?.product?.title,
              align: "top",
              image: _res?.product?.logo
            },
            buttons: this.getButtons(_res?.buttons),
            carouselItems: this.getCarousel(_res?.product),
            carouselStyles: config?.carouselStyles,
            accordionItems: this.getAccordionItems(_res),
            badgeCarouselItems: this.getBadgeCarousel(_res?.batchCredentials),
            TIME_LINE_ITEMS: this.getTimeLine(_res?.provenance),
            provenanceProdCreds: _res?.provenanceProdCreds,
            gtin: _res?.product?.gtin,
          }
        };
      }
    } catch (err: any) {
      console.log(`product Data`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
    // return { productData: { ...(config || {}) } };
  }

  private getTimeLine(data: any) {
    let _temp: any = [];
    Array.isArray(data) && data?.forEach(element => {
      _temp.push(
        {
          "key": element?._id,
          "headline": toHeaderCase(element?.name ?? ""),
          "headlineBackgroundColor": "#579783",
          "children": this.getChildren(element)
        }
      )
    });

    return _temp;
  }



  private getChildren(data: any) {
    let _temp: any = [];
    _temp.push({
      provenanceItem: data,
      multiColList: this.getProvenanceAccordion(data),
      "itemId": data?.provenanceId,
      "title": toHeaderCase(data?.name ?? ""),
      "subtitle": data?.description,
      "carouselItems": data?.media?.length > 0 ? [...data?.media] : null,
      "carouselStyles": {
        "width": "250px",
        "height": "250px"
      },
      "badgeCarouselItems": this.getBadgeCarousel(data?.credentials)
    });

    Array.isArray(data?.subSteps) && data?.subSteps.forEach(ele => {
      _temp.push({
        provenanceItem: ele,
        multiColList: this.getProvenanceAccordion(ele),
        "itemId": ele?.provenanceId,
        "title": toHeaderCase(ele?.name ?? ""),
        "subtitle": ele?.description,
        "carouselItems": ele?.media?.length > 0 ? [...ele?.media] : null,
        "carouselStyles": {
          "width": "250px",
          "height": "250px"
        },
        "badgeCarouselItems": this.getBadgeCarousel(ele?.credentials)
      })
    });

    return _temp;
  }

  private getBadgeCarousel(data: any) {
    let _temp: any = [];
    Array.isArray(data) && data.forEach(element => {
      _temp.push({
        batchData: element,
        key: element?.batchId,
        titleImage: element?.logo,
        title: element?.name
      });
    });

    return _temp;
  }

  private getCarousel(data: any) {
    const imgs = data?.images?.map((item: any) => {
      return ({
        type: "img",
        src: item
      });
    }) ?? [];
    const videos = data?.videos?.map((item: any) => {
      return ({
        type: "video",
        src: item?.src,
      });
    }) ?? [];
    const _carsoul = [...imgs, ...videos];
    return _carsoul?.length > 0 ? [...imgs, ...videos] : null;
  }

  private getAccordionItems(res: any) {
    let _temp = [];
    // if (res?.description) {
    //   _temp.push({
    //     type: "text",
    //     title: "Description",
    //     "data": [
    //       res?.description
    //     ]
    //   })
    // }

    if (res?.product?.variant?.attrs) {
      let _list: any = [];
      Array.isArray(res?.product?.variant?.attrs) && res?.product?.variant?.attrs.forEach((ele: any) => {
        _list.push({
          ...ele,
          title: toHeaderCase(ele?.name ?? ""),
          subTitle: ele?.val
        })
      });
      if (_list.length > 0) {
        _temp.push({
          "type": "multi-col-list",
          "title": "Product Details",
          "numberOfColumns": "3",
          data: [..._list]
        })
      }
    }

    if (res?.org) {
      let _list: any = [];
      let _org = { ...res?.org };
      delete _org._id;
      delete _org.id;
      delete _org.phones;
      delete _org.socialLinks;
      let _social = Array.isArray(res?.org?.socialLinks) && res?.org?.socialLinks?.map(item => {
        return ({
          title: toHeaderCase(item?.type ?? ""),
          subTitle: item?.url,
          link: item?.url
        });
      })
      for (const [key, value] of Object.entries(_org)) {
        _list.push({
          title: toHeaderCase(key ?? ""),
          subTitle: value
        })
      }
      _temp.push({
        "type": "multi-col-list",
        "title": "Organization",
        "numberOfColumns": "3",
        data: [..._list, ..._social]
      })
    }

    if (res?.blockchainProof && res?.blockchainProof?.length > 0) {
      let _list: any = [];
      Array.isArray(res?.blockchainProof) && res?.blockchainProof.forEach((ele: any) => {
        _list.push({
          ...ele,
        })
      });
      _temp.push({
        "type": "multi-col-list",
        "title": "Blockchain Proof",
        "numberOfColumns": "3",
        data: [..._list]
      })
    }
    return _temp;
  }

  private getProvenanceAccordion(res: any) {
    let _temp: any = [];
    if (res) {
      let _list: any = [];
      let _date: any = [];
      let _location: any = [];
      if (res?.org) {
        _list.push({
          title: toHeaderCase("by"),
          subTitle: res?.org?.name,
          icon: config?.orgIcon
        })
      }
      Array.isArray(res?.attributes) && res?.attributes?.forEach((ele: any) => {
        if (ele?.key.toLowerCase() === "location") {
          _location.push({
            title: toHeaderCase("at"),
            subTitle: ele?.value,
            icon: config?.locationIcon
          })
        }
        if (ele?.key.toLowerCase() === "date") {
          _date.push({
            title: toHeaderCase("on"),
            subTitle: ele?.value,
            icon: config?.dateIcon
          })
        }
      });
      _temp = [..._list, ..._location, ..._date]
    }
    return {
      "numberOfColumns": "2",
      data: _temp
    };
  }

  private getButtons(data: any) {
    let buttonColor = ["primary", "secondary", "tertiary"];
    let _buttons = Array.isArray(data) && data?.map((item, index) => {
      return ({
        ...item,
        key: `${item?.label}-${index}`,
        title: item?.name,
      });
    });
    return _buttons;
  }
}
